import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Upload, Image as ImageIcon, Save, RefreshCw, Snowflake } from 'lucide-react';
import { logger } from '@/lib/logger';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDefaultLogo } from '@/hooks/useDefaultLogo';
import { Switch } from '@/components/ui/switch';

export function PlatformSettingsManager() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { defaultLogoUrl, isLoading: logoLoading } = useDefaultLogo();
  const [logoUrl, setLogoUrl] = useState('');
  const [christmasThemeEnabled, setChristmasThemeEnabled] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Fetch current platform settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ['platform-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('*')
        .order('setting_key');

      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    if (settings) {
      const defaultLogoSetting = settings.find(s => s.setting_key === 'default_logo_url');
      if (defaultLogoSetting) {
        setLogoUrl(defaultLogoSetting.setting_value);
      }
      
      const christmasThemeSetting = settings.find(s => s.setting_key === 'christmas_theme_enabled');
      if (christmasThemeSetting) {
        setChristmasThemeEnabled(christmasThemeSetting.setting_value === 'true');
      }
    }
  }, [settings]);

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload an image file',
        variant: 'destructive',
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Please upload an image smaller than 5MB',
        variant: 'destructive',
      });
      return;
    }

    setIsUploading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Verify user is system admin
      if (user.email?.toLowerCase() !== 'admin@northreports.com') {
        throw new Error('Only system admin (admin@northreports.com) can upload platform logos');
      }

      // Upload to Supabase Storage in a special 'platform' folder
      const fileExt = file.name.split('.').pop();
      const fileName = `platform/default-logo-${Date.now()}.${fileExt}`;
      const filePath = fileName;

      logger.debug('Uploading logo to:', filePath);
      logger.debug('User email:', user.email);
      logger.debug('User ID:', user.id);

      // First, verify we can access the bucket
      const { data: bucketData, error: bucketError } = await supabase.storage
        .from('logos')
        .list('platform', { limit: 1 });

      if (bucketError) {
        logger.warn('Bucket access check:', bucketError);
      } else {
        logger.debug('Bucket access OK, existing files:', bucketData);
      }

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('logos')
        .upload(filePath, file, { upsert: true });

      if (uploadError) {
        const errorDetails = {
          message: uploadError.message,
          statusCode: uploadError.statusCode,
          errorCode: uploadError.error,
          userEmail: user.email,
          filePath: filePath,
          fullError: uploadError
        };
        
        logger.error('Storage upload error details:', errorDetails);
        
        // Show detailed error in toast
        const errorMessage = uploadError.message || uploadError.error || 'Unknown error';
        const errorText = `Upload failed: ${errorMessage}${uploadError.statusCode ? ` (Status: ${uploadError.statusCode})` : ''}`;
        
        toast({
          title: 'Upload Error',
          description: errorText,
          variant: 'destructive',
        });
        
        throw new Error(errorText);
      }

      logger.debug('Upload successful:', uploadData);

      const { data: { publicUrl } } = supabase.storage
        .from('logos')
        .getPublicUrl(filePath);

      // Ensure the URL has the correct format with /public/ in the path
      // Fix for custom domains that might not include /public/ correctly
      let correctedUrl = publicUrl;
      if (publicUrl.includes('/storage/v1/object/') && !publicUrl.includes('/storage/v1/object/public/')) {
        correctedUrl = publicUrl.replace('/storage/v1/object/', '/storage/v1/object/public/');
      }

      setLogoUrl(correctedUrl);
      toast({
        title: 'Success',
        description: 'Logo uploaded successfully. Click Save to apply.',
      });
    } catch (error) {
      logger.error('Error uploading logo:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to upload logo',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Update or insert both settings
      const { error } = await supabase
        .from('platform_settings')
        .upsert([
          {
            setting_key: 'default_logo_url',
            setting_value: logoUrl,
            description: 'Default North Reports logo URL. Can be changed to holiday themes or other variations without code deployment.',
          },
          {
            setting_key: 'christmas_theme_enabled',
            setting_value: christmasThemeEnabled ? 'true' : 'false',
            description: 'Enable Christmas theme (snow and lights) on the landing page.',
          }
        ], {
          onConflict: 'setting_key',
        });

      if (error) throw error;

      // Invalidate queries to refresh settings everywhere
      queryClient.invalidateQueries({ queryKey: ['platform-settings'] });
      queryClient.invalidateQueries({ queryKey: ['platform-settings', 'default_logo_url'] });
      queryClient.invalidateQueries({ queryKey: ['platform-settings', 'christmas_theme_enabled'] });

      toast({
        title: 'Success',
        description: 'Platform settings updated successfully. Changes will appear immediately.',
      });
    } catch (error) {
      logger.error('Error saving platform settings:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save settings',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    const defaultUrl = 'https://zkqcqgjbsigjdwzmnwmp.supabase.co/storage/v1/object/public/logos/north-logo.png';
    setLogoUrl(defaultUrl);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Default Logo Management</CardTitle>
          <CardDescription>
            Manage the default North Reports logo that appears across the platform when users haven't uploaded a custom logo.
            You can hot-swap this for holiday themes or special promotions without code changes.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Current Logo Preview */}
          <div className="space-y-2">
            <Label>Current Default Logo</Label>
            <div className="flex items-center gap-4 p-4 border rounded-lg bg-muted/50">
              {logoLoading ? (
                <Loader2 className="h-12 w-auto animate-spin text-muted-foreground" />
              ) : (
                <img 
                  src={defaultLogoUrl} 
                  alt="Current default logo" 
                  className="h-12 w-auto max-w-xs object-contain"
                />
              )}
            </div>
          </div>

          {/* Logo URL Input */}
          <div className="space-y-2">
            <Label htmlFor="logo-url">Logo URL</Label>
            <div className="flex gap-2">
              <Input
                id="logo-url"
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                placeholder="https://..."
                className="flex-1"
              />
              <Button
                variant="outline"
                onClick={handleReset}
                title="Reset to default URL"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Enter a URL to a logo hosted in Supabase Storage or any public URL
            </p>
          </div>

          {/* Upload New Logo */}
          <div className="space-y-2">
            <Label htmlFor="logo-upload">Upload New Logo</Label>
            <div className="flex items-center gap-4">
              <Input
                id="logo-upload"
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                disabled={isUploading}
                className="flex-1"
              />
              {isUploading && (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              Upload a new logo to Supabase Storage (max 5MB, PNG/JPG recommended)
            </p>
          </div>

          {/* Preview New Logo */}
          {logoUrl && logoUrl !== defaultLogoUrl && (
            <div className="space-y-2">
              <Label>Preview New Logo</Label>
              <div className="flex items-center gap-4 p-4 border rounded-lg bg-muted/50">
                <img 
                  src={logoUrl} 
                  alt="New logo preview" 
                  className="h-12 w-auto max-w-xs object-contain"
                  onError={() => {
                    toast({
                      title: 'Invalid URL',
                      description: 'The logo URL could not be loaded. Please check the URL.',
                      variant: 'destructive',
                    });
                  }}
                />
              </div>
            </div>
          )}

          {/* Save Button */}
          <div className="flex justify-end gap-2">
            <Button
              onClick={handleSave}
              disabled={isSaving || !logoUrl}
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Christmas Theme Toggle */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Snowflake className="h-5 w-5" />
            Christmas Theme
          </CardTitle>
          <CardDescription>
            Enable or disable the Christmas theme (falling snow and lights) on the landing page.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="christmas-theme" className="text-base">
                Enable Christmas Theme
              </Label>
              <p className="text-sm text-muted-foreground">
                Show falling snow and animated Christmas lights on the landing page
              </p>
            </div>
            <Switch
              id="christmas-theme"
              checked={christmasThemeEnabled}
              onCheckedChange={setChristmasThemeEnabled}
            />
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
